import { supabase } from "~/integrations/supabase/client";
import {
  CreatePaymentProofData,
  PaymentProof,
  UpdatePaymentProofData,
} from "~/types/payment";

/**
 * Fetch payment proofs for a client
 */
export const getClientPaymentProofs = async (
  clientId: string
): Promise<PaymentProof[]> => {
  try {
    const { data, error } = await supabase
      .from("payment_proofs")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    const paymentProofs: PaymentProof[] = data.map((item) => ({
      id: item.id,
      createdAt: item.created_at,
      updatedAt: item.updated_at || undefined,
      createdBy: item.created_by,
      clientId: item.client_id,
      rut: item.rut,
      amount: item.amount,
      screenshotUrl: item.screenshot_url || undefined,
      salesRequestId: item.sales_request_id || undefined,
      comments: item.comments || undefined,
    }));

    return paymentProofs;
  } catch (error) {
    console.error("Error fetching payment proofs:", error);
    return [];
  }
};

/**
 * Get a single payment proof by ID
 */
export const getPaymentProofById = async (
  id: string
): Promise<PaymentProof | null> => {
  try {
    const { data, error } = await supabase
      .from("payment_proofs")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;

    return {
      id: data.id,
      createdAt: data.created_at,
      updatedAt: data.updated_at || undefined,
      createdBy: data.created_by,
      clientId: data.client_id,
      rut: data.rut,
      amount: data.amount,
      screenshotUrl: data.screenshot_url || undefined,
      salesRequestId: data.sales_request_id || undefined,
      comments: data.comments || undefined,
    };
  } catch (error) {
    console.error("Error fetching payment proof:", error);
    return null;
  }
};

/**
 * Create a new payment proof
 */
export const createPaymentProof = async (
  paymentProof: CreatePaymentProofData
): Promise<PaymentProof | null> => {
  try {
    const { data, error } = await supabase
      .from("payment_proofs")
      .insert({
        client_id: paymentProof.clientId,
        rut: paymentProof.rut,
        amount: paymentProof.amount,
        screenshot_url: paymentProof.screenshotUrl || null,
        sales_request_id: paymentProof.salesRequestId || null,
        comments: paymentProof.comments || null,
      })
      .select()
      .single();

    if (error) throw error;

    // If payment proof is linked to a sales request, check if we need to update the status
    if (paymentProof.salesRequestId) {
      try {
        // Check if the sales request is in "prepaid" status
        const { data: salesRequest, error: salesRequestError } = await supabase
          .from("sales_request")
          .select("status")
          .eq("id", paymentProof.salesRequestId)
          .single();

        if (
          !salesRequestError &&
          salesRequest &&
          salesRequest.status === "prepaid"
        ) {
          // Keep prepaid orders in prepaid status but prevent auto-paid
          const { error: updateError } = await supabase
            .from("sales_request")
            .update({
              paid: false, // Explicitly prevent auto-paid for prepaid workflow
              updated_at: new Date().toISOString(),
            })
            .eq("id", paymentProof.salesRequestId);

          if (updateError) {
            console.error("Error updating sales request status:", updateError);
          }
        }
      } catch (statusUpdateError) {
        console.error(
          "Error checking/updating sales request status:",
          statusUpdateError
        );
        // Don't fail the payment proof creation if status update fails
      }
    }

    return {
      id: data.id,
      createdAt: data.created_at,
      updatedAt: data.updated_at || undefined,
      createdBy: data.created_by,
      clientId: data.client_id,
      rut: data.rut,
      amount: data.amount,
      screenshotUrl: data.screenshot_url || undefined,
      salesRequestId: data.sales_request_id || undefined,
      comments: data.comments || undefined,
    };
  } catch (error) {
    console.error("Error creating payment proof:", error);
    return null;
  }
};

/**
 * Update an existing payment proof
 */
export const updatePaymentProof = async (
  id: string,
  updates: UpdatePaymentProofData
): Promise<PaymentProof | null> => {
  try {
    const updateData: any = {};

    if (updates.rut !== undefined) updateData.rut = updates.rut;
    if (updates.amount !== undefined) updateData.amount = updates.amount;
    if (updates.screenshotUrl !== undefined)
      updateData.screenshot_url = updates.screenshotUrl;
    if (updates.salesRequestId !== undefined)
      updateData.sales_request_id = updates.salesRequestId;
    if (updates.comments !== undefined) updateData.comments = updates.comments;

    const { data, error } = await supabase
      .from("payment_proofs")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      createdAt: data.created_at,
      updatedAt: data.updated_at || undefined,
      createdBy: data.created_by,
      clientId: data.client_id,
      rut: data.rut,
      amount: data.amount,
      screenshotUrl: data.screenshot_url || undefined,
      salesRequestId: data.sales_request_id || undefined,
      comments: data.comments || undefined,
    };
  } catch (error) {
    console.error("Error updating payment proof:", error);
    return null;
  }
};

/**
 * Delete a payment proof
 */
export const deletePaymentProof = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("payment_proofs")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error deleting payment proof:", error);
    return false;
  }
};

/**
 * Upload a screenshot image for a payment proof
 */
export const uploadPaymentScreenshot = async (
  file: File,
  clientId: string
): Promise<string | null> => {
  try {
    // First check if the bucket exists
    const { data: buckets, error: bucketError } =
      await supabase.storage.listBuckets();

    if (bucketError) {
      console.error("Error checking buckets:", bucketError);
      throw new Error(`Unable to access storage: ${bucketError.message}`);
    }

    // Check if 'uploads' bucket exists
    const uploadsBucket = buckets.find((bucket) => bucket.name === "uploads");
    if (!uploadsBucket) {
      console.error("Uploads bucket not found");
      throw new Error("Storage bucket 'uploads' not found");
    }

    // Generate a unique file path
    const fileExt = file.name.split(".").pop() || "jpg";
    const fileName = `${clientId}/${Date.now()}.${fileExt}`;
    const filePath = `payment-proofs/${fileName}`;

    // Prepare for upload with more explicit error handling
    console.log(`Uploading file to: ${filePath}`);
    const { error: uploadError } = await supabase.storage
      .from("uploads")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) {
      console.error("Upload error details:", uploadError);
      throw new Error(`Failed to upload: ${uploadError.message}`);
    }

    // Get the public URL
    const { data: urlData } = supabase.storage
      .from("uploads")
      .getPublicUrl(filePath);

    if (!urlData || !urlData.publicUrl) {
      throw new Error("Failed to get public URL for uploaded file");
    }

    console.log("Upload successful, public URL:", urlData.publicUrl);
    return urlData.publicUrl;
  } catch (error) {
    console.error("Error in uploadPaymentScreenshot:", error);
    return null;
  }
};

/**
 * Get payment proofs related to a sales request
 */
export const getSalesRequestPaymentProofs = async (
  salesRequestId: string
): Promise<PaymentProof[]> => {
  try {
    const { data, error } = await supabase
      .from("payment_proofs")
      .select("*")
      .eq("sales_request_id", salesRequestId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    const paymentProofs: PaymentProof[] = data.map((item) => ({
      id: item.id,
      createdAt: item.created_at,
      updatedAt: item.updated_at || undefined,
      createdBy: item.created_by,
      clientId: item.client_id,
      rut: item.rut,
      amount: item.amount,
      screenshotUrl: item.screenshot_url || undefined,
      salesRequestId: item.sales_request_id || undefined,
      comments: item.comments || undefined,
    }));

    return paymentProofs;
  } catch (error) {
    console.error("Error fetching payment proofs for sales request:", error);
    return [];
  }
};
